import React, { useRef, useState, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import {
  getDividedOptions,
  getOptionValues,
  getSafetyValues,
  getSortedValues,
} from '@gp/components/SelectMultiple/utils';

import type { ChangeContext } from 'tea-component';

export default function useCustomizeTagSelect(props?: {
  loading?: boolean;
  required?: boolean;
  sortable?: boolean;
  options?: IGPSelectOptionWithGroup[] | null;
  initialOptions?: IGPSelectOptionWithGroup[] | null;
  initialValue?: string[] | null;
  onChange?: IGPSelectMultipleChangeHandler;
  // onChange回调的前置处理函数，该函数返回值代表是否继续执行父方法剩余流程，该函数返回true时，继续执行onChange的处理，返回false时，则提前终止onChange的处理
  onChangePreProcess?: (
    payload: string[],
    context: ChangeContext<React.SyntheticEvent<Element, Event>> | null,
  ) => boolean;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const {
    loading,
    required,
    sortable,
    options,
    initialOptions,
    initialValue,
    onChange,
    onChangePreProcess,
    onOpen,
    onClose,
  } = props || {};

  const initialized = useRef(false);

  const [dividedOptions, setDividedOptions] = useState<IGPSelectOptionWithGroup[]>([]);

  const [value, setValue] = useState<string[]>([]);

  const emitValue: IGPSelectMultipleChangeHandler = (payload, context) => {
    if (isEqual(payload, initialValue)) return;
    onChange?.(payload, context);
  };

  const getEmitValue = (payload?: string[] | null): string[] => {
    let payloadTmp = getSafetyValues(!!required, payload, options);
    if (sortable) {
      payloadTmp = getSortedValues(!!required, payloadTmp, options);
    }
    return payloadTmp;
  };

  const handleChange: IGPSelectMultipleChangeHandler = (payload, context) => {
    /**
     * 这里必须判断严格等于false
     * 因为onChangePreProcess方法为可选属性，当调用者没有传onChangePreProcess属性时，不严格判断返回值为false的话，这里逻辑就错了
     * 仅当调用者有传onChangePreProcess方法，并且onChangePreProcess方法返回false时，才提前终止
     */
    if (onChangePreProcess?.(payload, context) === false) return;
    const payloadTmp = !payload ? [] : payload;
    setValue(payloadTmp);
  };

  const handleOpen = () => {
    const dividedOptionsTmp = getDividedOptions(options, value);
    setDividedOptions(dividedOptionsTmp);
    onOpen?.();
  };

  const handleClose = () => {
    const valueTmp = getEmitValue(value);
    emitValue(valueTmp, null);
    setValue(valueTmp);
    onClose?.();
  };

  useEffect(() => {
    if (loading) return;
    if (initialized.current) return;
    const allValues = getOptionValues(initialOptions);
    let validValue = initialValue?.filter((item) => allValues?.includes(item)) || [];
    if (required && !validValue?.length) {
      validValue = [...allValues];
    }
    if (!isEqual(value, validValue)) {
      setValue(validValue);
    }
    initialized.current = true;
  }, [loading, initialOptions, initialValue]);

  useEffect(() => {
    setDividedOptions(initialOptions || []);
  }, [initialOptions]);

  return {
    dividedOptions,
    setDividedOptions,
    value,
    setValue,
    onChange: handleChange,
    onOpen: handleOpen,
    onClose: handleClose,
  };
}
